import type {
  AccordionRootProps as ArkAccordionRootProps,
  AccordionItemProps as ArkAccordionItemProps,
} from "@ark-ui/react";
import { Accordion as ArkAccordion } from "@ark-ui/react";
import Icon from "../Icon/Icon";
import {
  aromaAccordion,
  AromaAccordionVariantProps,
} from "@zenchef/styled-system/recipes";
import { forwardRef } from "react";
import type {
  ElementRef,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefAttributes,
} from "react";
import { Text } from "../typography/Text";
import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import { Divider, HStack, VStack } from "@zenchef/styled-system/jsx";
import { cx } from "@zenchef/styled-system/css";
import Tooltip from "../Tooltip/Tooltip";
import { Badge, BadgeProps, ToggleSwitch, ToggleSwitchProps } from "..";
import withTestId from "../../utils/withTestId";

interface AccordionItemProps extends ArkAccordionItemProps {
  label: string;
  iconName?: IconName;
  hint?: string;
  tooltip?: string;
  count?: number;
  toggleProps?: ToggleSwitchProps;
  badgeProps?: BadgeProps;
  triggerContent?: ReactNode;
  withBorder?: boolean;
}

const AccordionItem = ({
  children,
  label,
  iconName,
  hint,
  tooltip,
  toggleProps,
  badgeProps,
  triggerContent,
  ...propsWithVariants
}: AccordionItemProps) => {
  const [variants, props] = aromaAccordion.splitVariantProps(propsWithVariants);
  const classes = aromaAccordion(variants);

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
    toggleProps?.onClick?.(event);
  };

  return (
    <ArkAccordion.Item {...props} className={classes.item}>
      <ArkAccordion.ItemTrigger className={classes.trigger}>
        <HStack
          justifyContent="space-between"
          alignItems="center"
          gap="gap.2"
          width="100%"
        >
          <HStack gap="gap.2" alignItems="center">
            {toggleProps ? (
              <ToggleSwitch
                disabled={props.disabled}
                {...toggleProps}
                onClick={handleClick}
              />
            ) : null}
            {iconName && !toggleProps ? (
              <Icon fontSize="125" name={iconName} />
            ) : null}
            <HStack gap="gap.1" alignItems="center">
              <Text className={classes.itemText}>{label}</Text>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <Icon
                    name="info-circle"
                    fontSize="{sizes.xs}"
                    color={
                      props.disabled
                        ? "content.neutral.base.disable"
                        : "content.neutral.base.bold"
                    }
                  />
                </Tooltip>
              ) : null}
            </HStack>
          </HStack>
          <ArkAccordion.ItemIndicator className={classes.indicator}>
            {triggerContent ? <>{triggerContent}</> : null}
            {badgeProps ? <Badge {...badgeProps} /> : null}
            <ArkAccordion.Context>
              {(context) => (
                <Icon
                  fontSize="{sizes.xs}"
                  name={
                    context.value.includes(props.value)
                      ? "chevron-down"
                      : "chevron-right"
                  }
                />
              )}
            </ArkAccordion.Context>
          </ArkAccordion.ItemIndicator>
        </HStack>
        {hint ? (
          <Text
            textStyle="mono.s.regular"
            color="content.neutral.base.subtlest"
            textAlign="left"
          >
            {hint}
          </Text>
        ) : null}
      </ArkAccordion.ItemTrigger>
      <ArkAccordion.Context>
        {(context) =>
          context.value.includes(props.value) ? (
            <ArkAccordion.ItemContent className={classes.content}>
              {children}
            </ArkAccordion.ItemContent>
          ) : null
        }
      </ArkAccordion.Context>
    </ArkAccordion.Item>
  );
};

interface AccordionRootProps
  extends Omit<ArkAccordionRootProps, "children">,
    AromaAccordionVariantProps {
  items: (AccordionItemProps & { hideDivider?: boolean })[];
  className?: string;
  withBorder?: boolean;
}

const AccordionRoot = forwardRef<ElementRef<"div">, AccordionRootProps>(
  ({ items, className, ...propsWithVariants }, ref) => {
    const [variants, props] =
      aromaAccordion.splitVariantProps(propsWithVariants);
    const classes = aromaAccordion(variants);

    return (
      <ArkAccordion.Root
        ref={ref}
        {...props}
        className={cx(classes.root, className)}
      >
        {items.map(({ hideDivider, ...item }) => (
          <VStack key={item.value}>
            <AccordionItem
              disabled={props.disabled}
              withBorder={propsWithVariants?.withBorder}
              {...item}
            />
            {!hideDivider && !item.hidden ? (
              <Divider borderColor="border.neutral.bold" />
            ) : null}
          </VStack>
        ))}
      </ArkAccordion.Root>
    );
  },
) as (
  props: AccordionRootProps & RefAttributes<ElementRef<"div">>,
) => ReactElement;

export type { AccordionRootProps as AccordionProps };
export default withTestId("list")(AccordionRoot);
